import type { InvoiceRepository } from '../../application/InvoiceRepository'
import type { InvoiceDetailResult, InvoicePage } from '../../domain/invoice'

/**
 * Aucune facture n'est simulée : un montant inventé sur un écran financier
 * serait pris pour une créance réelle.
 */
export function createFixtureInvoiceRepository(
  source: 'fixtures' | 'fixture-fallback' = 'fixtures',
): InvoiceRepository {
  const meta = { source, retrievedAt: new Date().toISOString() }
  return {
    list(filters): Promise<InvoicePage> {
      return Promise.resolve({
        items: [],
        page: filters.page,
        pageSize: filters.pageSize,
        total: 0,
        stats: {
          total: 0,
          draft: 0,
          issued: 0,
          partiallyPaid: 0,
          paid: 0,
          overdue: 0,
          cancelled: 0,
          outstandingCents: 0,
        },
        meta,
      })
    },
    getByReference(): Promise<InvoiceDetailResult | null> {
      return Promise.resolve(null)
    },
    issue() {
      return Promise.reject(new Error('INVOICE_ISSUE_SUPABASE_REQUIRED'))
    },
    cancel() {
      return Promise.reject(new Error('INVOICE_CANCEL_SUPABASE_REQUIRED'))
    },
    listPayments() {
      return Promise.resolve([])
    },
    listRecentPayments(filters) {
      return Promise.resolve({
        items: [],
        page: filters.page,
        pageSize: filters.pageSize,
        total: 0,
        recorded: 0,
        cancelled: 0,
        meta: { source: 'fixtures' as const, retrievedAt: new Date().toISOString() },
      })
    },
    searchOpenInvoices() {
      return Promise.resolve([])
    },
    sendCreditNote() {
      return Promise.reject(new Error('CREDIT_NOTE_SEND_SUPABASE_REQUIRED'))
    },
    sendReceipt() {
      return Promise.reject(new Error('RECEIPT_SEND_SUPABASE_REQUIRED'))
    },
    listCreditNotes() {
      return Promise.resolve([])
    },
    createCreditNote() {
      return Promise.reject(new Error('CREDIT_CREATE_SUPABASE_REQUIRED'))
    },
    transitionCreditNote() {
      return Promise.reject(new Error('CREDIT_TRANSITION_SUPABASE_REQUIRED'))
    },
    recordPayment() {
      return Promise.reject(new Error('PAYMENT_RECORD_SUPABASE_REQUIRED'))
    },
    cancelPayment() {
      return Promise.reject(new Error('PAYMENT_CANCEL_SUPABASE_REQUIRED'))
    },
    getClientCreditAccount(clientId: string) {
      // Un compte de crédit fictif ferait croire à une avance inexistante.
      return Promise.resolve({ clientId, balanceCents: 0, entries: [] })
    },
    applyClientCredit() {
      return Promise.reject(new Error('CREDIT_APPLY_SUPABASE_REQUIRED'))
    },
    listItems() {
      return Promise.resolve([])
    },
    // Une écriture ne s'invente pas hors ligne : elle remonte l'absence.
    replaceItems() {
      return Promise.reject(new Error('INVOICE_ITEMS_REPLACE_SUPABASE_REQUIRED'))
    },
    send() {
      return Promise.reject(new Error('INVOICE_SEND_SUPABASE_REQUIRED'))
    },
    getBillingKpis() {
      // Aucun KPI financier n'est simulé : `null` dit « non disponible »,
      // jamais un zéro qui laisserait croire à un solde à jour inexistant.
      return Promise.resolve({
        toCollectCents: null,
        overdueCents: null,
        collectedThisMonthCents: null,
        openInvoicesCount: null,
        vipTotalCents: null,
        vipCount: null,
        meta: { source: 'fixtures' as const, retrievedAt: new Date().toISOString() },
      })
    },
    listPaymentsForClient() {
      return Promise.resolve([])
    },
  }
}
